import { useTranslation } from 'react-i18next';
import { latestArticles, popularArticles, guides, safetyArticles, seoArticles } from '@/mocks/articles';
import { Link } from 'react-router-dom';

export default function ArticleSection() {
  const { t } = useTranslation('home');

  return (
    <section className="py-14 md:py-20 bg-background-100/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            {t('article_title')}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base font-body">
            {t('article_subtitle')}
          </p>
        </div>

        {/* 9 SEO Articles - Main Featured Grid */}
        <div className="mb-14">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            Artykuły SEO
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {seoArticles.map((article) => (
              <Link
                key={article.seq}
                to={`/${article.slug}`}
                className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 flex flex-col cursor-pointer"
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    title={`${article.title} - Kakobuy Spreadsheet`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <span className="text-xs text-primary-500 font-label mb-2">{article.category}</span>
                  <h4 className="font-heading text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                    {article.title}
                  </h4>
                  <p className="text-xs text-foreground-400 leading-relaxed line-clamp-2 mb-3 font-body">
                    {article.excerpt}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-1 text-xs text-foreground-500 group-hover:text-foreground-950 font-label whitespace-nowrap transition-colors duration-200">
                    Czytaj więcej <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Guides Row */}
        <div className="mb-14">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            Poradniki
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
            {guides.map((guide) => (
              <Link
                key={guide.seq}
                to={`/${guide.slug}`}
                className="flex items-center gap-3 px-4 py-3 bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-200/60 hover:bg-primary-50/20 transition-all duration-200 group cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                  <i className="ri-book-open-line text-sm text-accent-600"></i>
                </div>
                <div>
                  <p className="text-sm font-label font-medium text-foreground-800 group-hover:text-foreground-950 whitespace-nowrap transition-colors duration-200">
                    {guide.title}
                  </p>
                  <p className="text-xs text-foreground-400 mt-0.5 font-body">{guide.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Safety Section */}
        <div className="mb-14">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            Bezpieczeństwo
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
            {safetyArticles.map((article) => (
              <Link
                key={article.seq}
                to={`/${article.slug}`}
                className="flex items-center gap-3 px-4 py-3.5 bg-background-50 rounded-xl border border-background-200/60 hover:border-accent-200/60 hover:bg-accent-50/20 transition-all duration-200 group cursor-pointer"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                  <i className="ri-shield-check-line text-base text-accent-600"></i>
                </div>
                <div>
                  <p className="text-sm font-label font-medium text-foreground-800 group-hover:text-foreground-950 whitespace-nowrap transition-colors duration-200">
                    {article.title}
                  </p>
                  <p className="text-xs text-foreground-400 mt-0.5 font-body">{article.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Latest Articles */}
        <div className="mb-10">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            Najnowsze artykuły
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {latestArticles.slice(0, 4).map((article) => (
              <Link
                key={article.seq}
                to={`/${article.slug}`}
                className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 flex flex-col cursor-pointer"
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    title={`${article.title} - Kakobuy Spreadsheet`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <span className="text-xs text-primary-500 font-label mb-2">{article.category}</span>
                  <h4 className="font-heading text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                    {article.title}
                  </h4>
                  <p className="text-xs text-foreground-400 leading-relaxed line-clamp-2 mb-3 font-body">
                    {article.excerpt}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-1 text-xs text-foreground-500 group-hover:text-foreground-950 font-label whitespace-nowrap transition-colors duration-200">
                    Czytaj więcej <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Popular Articles link list */}
        <div>
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
            Popularne artykuły
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {popularArticles.slice(0, 8).map((article) => (
              <Link
                key={article.seq}
                to={`/${article.slug}`}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-background-50 rounded-lg transition-colors duration-200 group cursor-pointer"
              >
                <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0"></div>
                <span className="text-sm text-foreground-600 group-hover:text-foreground-950 font-body transition-colors duration-200 line-clamp-1">
                  {article.title}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors duration-200 font-label font-medium text-sm whitespace-nowrap cursor-pointer group"
          >
            {t('btn_view_all')}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200"></i>
          </a>
        </div>
      </div>
    </section>
  );
}